import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdSlot } from '../components/AdSlot';
import { RecipeCard } from '../components/RecipeCard';
import { SearchBar } from '../components/SearchBar';
import { recipes } from '../data/recipes';
import { searchRecipes } from '../lib/search';
import { usePageMeta } from '../lib/meta';

const FEATURED_SLUGS = [
  'the-classic-peacock-omelette',
  'midnight-truffle-peacock-omelette',
  'crispy-thai-peacock-omelette',
  'souffle-peacock-omelette',
  'brie-and-fig-peacock-omelette',
  'the-humble-peacock-omelette',
];

export function HomePage() {
  usePageMeta({
    title: 'Distinguished Peacock Omelette Recipes',
    description:
      'Twenty time-honored peacock omelette recipes, tested in our own kitchen and organized by occasion. Searchable by ingredient.',
    path: '/',
  });

  const [query, setQuery] = useState('');
  const results = useMemo(() => searchRecipes(recipes, query), [query]);
  const searching = query.trim().length > 0;
  const featured = useMemo(
    () => FEATURED_SLUGS.map((slug) => recipes.find((r) => r.slug === slug)!).filter(Boolean),
    [],
  );

  return (
    <>
      {/* Hero */}
      <section className="bg-iridescent relative overflow-hidden">
        <div className="bg-sheen pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-gold-300">
            Est. 2026 &middot; Twenty recipes &middot; One bird
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl leading-tight text-white sm:text-6xl">
            Distinguished omelettes, from history&rsquo;s most decorated bird.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-plume-100">
            For too long, the peacock omelette has lived in the margins of serious cuisine. We
            publish the definitive collection: tested in our own kitchen, seasoned with restraint,
            and organized by occasion.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/recipes" className="btn-gloss">
              Browse the collection
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center rounded-full border-2 border-white/30 px-6 py-3 font-semibold text-white transition-colors hover:border-white/60 hover:bg-white/10"
            >
              Our story
            </Link>
          </div>
        </div>
      </section>

      {/* Search */}
      <section aria-labelledby="search-heading" className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <h2 id="search-heading" className="sr-only">
          Search recipes
        </h2>
        <SearchBar
          value={query}
          onChange={setQuery}
          resultCount={searching ? results.length : recipes.length}
          className="mx-auto max-w-2xl"
        />

        {searching ? (
          results.length > 0 ? (
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((recipe) => (
                <RecipeCard key={recipe.slug} recipe={recipe} />
              ))}
            </div>
          ) : (
            <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-gloss">
              <p className="font-display text-xl text-plume-900">
                No recipe matches &ldquo;{query.trim()}&rdquo;.
              </p>
              <p className="mt-3 text-plume-700">
                The collection is curated, not infinite. Try an ingredient (&ldquo;gruyère&rdquo;,
                &ldquo;truffle&rdquo;, &ldquo;potato&rdquo;), or submit your suggestion to the void;
                we check the void seasonally.
              </p>
            </div>
          )
        ) : (
          <>
            <div className="mt-12 flex items-baseline justify-between">
              <h2 className="font-display text-2xl text-plume-950 sm:text-3xl">
                Editor&rsquo;s selections
              </h2>
              <Link
                to="/recipes"
                className="font-semibold text-plume-700 underline-offset-4 hover:text-plume-900 hover:underline"
              >
                All twenty →
              </Link>
            </div>
            <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((recipe) => (
                <RecipeCard key={recipe.slug} recipe={recipe} />
              ))}
            </div>
          </>
        )}
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AdSlot slot="display" />
      </div>

      {/* Test kitchen notes */}
      <section aria-labelledby="notes-heading" className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
        <h2 id="notes-heading" className="font-display text-2xl text-plume-950 sm:text-3xl">
          From the test kitchen
        </h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-3">
          <div className="rounded-2xl bg-white p-6 shadow-gloss">
            <h3 className="font-display text-lg text-plume-900">On scale</h3>
            <p className="mt-2 text-sm leading-relaxed text-plume-800">
              A peafowl egg weighs roughly ninety grams, the working equivalent of two large
              chicken eggs in one confident shell. Recipes here are scaled accordingly. Adjust your
              ambitions, not our quantities.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-gloss">
            <h3 className="font-display text-lg text-plume-900">On season</h3>
            <p className="mt-2 text-sm leading-relaxed text-plume-800">
              Peafowl lay from spring into early summer, and not one day longer. We freeze nothing,
              rush nothing, and spend our winters on technique and correspondence with suppliers.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-gloss">
            <h3 className="font-display text-lg text-plume-900">On the name</h3>
            <p className="mt-2 text-sm leading-relaxed text-plume-800">
              Readers periodically write to note that peacocks are male and do not lay eggs. This is
              correct. The eggs are the peahen&rsquo;s; the omelette is named for the audience. We
              consider the matter settled.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
